import React, { type FC, useContext, type PropsWithChildren } from 'react';

import { mqMin } from '@lucid/theme';
import { isBrowser, getI18nData } from '@lucid/utils';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

import AlertBanner from '../alert/alert-banner';
import Container from '../../container/container';
import LayoutContext from '../../context/layout-context';
import type { LinkProps } from '../../link/link';
import type { Lucid } from '../../../types/types';

type Announcement = {
  /** The text for the link on the Annoucement Banner. */
  linkText?: string;
  /** The url for the link on the Announcement Banner. */
  linkUrl?: string;
  /** The storageKey to save in localStorage. */
  storageKey?: string;
  /** The content of the message in the Announcement Banner. */
  content?: string;
  /** The Utah day and time to start showing this announcement. */
  start?: dayjs.Dayjs;
  /** The Utah day and time to stop showing this announcement. */
  end?: dayjs.Dayjs;
};

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.tz.setDefault('America/Denver');

export interface AnnouncementBannerProps {
  /** Allows the component to get additional styles. */
  className?: string;
  /** The specific site. */
  product: Lucid.Product.Sites | 'test';
  /** The paths where the banner will be included. */
  allowedPaths?: string[];
  /** The i18n translation data. */
  i18nData?: Lucid.I18n.DataEdges;
}

const AnnouncementBanner: FC<PropsWithChildren<AnnouncementBannerProps>> = ({
  className,
  i18nData,
  product,
  ...rest
}) => {
  const { shortCode } = useContext(LayoutContext);
  const i18n = getI18nData(i18nData, shortCode);

  const pathTranslations: Record<Lucid.I18n.LanguagesShortCode, string | undefined> = {
    en: '/blog/lately-at-lucid-spring-2024',
    fr: '/fr/blog/dernierement-chez-lucid-printemps-2024',
    da: '/blog/lately-at-lucid-spring-2024',
    de: '/de/blog/in-letzter-zeit-bei-lucid-fruehjahr-2024',
    es: '/es/blog/novedades-de-lucid-primavera-2024',
    nl: '/nl/blog/onlangs-bij-lucid-voorjaar-2024',
    it: '/it/blog/novita-dalucid-primavera-2024',
    pt: '/pt/blog/novidades-na-lucid-segundo-trimestre-2024',
    ja: '/ja/blog/lately-at-lucid-spring-2024',
    ko: '/ko/blog/lately-at-lucid-spring-2024',
    pl: '/pl/blog/nowosci-lucid-wiosna-2024',
    ru: '/ru/blog/lately-at-lucid-spring-2024',
    sv: '/sv/blog/nytt-fran-lucid-varen-2024',
  };

  const i18nStrings = {
    linkText: i18n('mktg-announcement-whats-new', {}, [], true),
    content: i18n('mktg-announcement-lately-at-lucid', {}, [], true),
  };

  const announcements: Partial<Record<Lucid.Product.Sites | 'default' | 'test', Announcement>> = {
    // test is here just as an example of data that should be included.
    test: {
      linkText: 'Test linkText',
      linkUrl: 'https://lucid.co/careers',
      storageKey: 'test-announcement-key',
      content: 'Test announcement banner text!',
      // set to Utah time
      start: dayjs.tz('2024-01-01T11:06:00.000'),
      // set to Utah time
      end: dayjs('2030-01-01T11:06:30.000'),
    },
    default: {
      linkText: i18nStrings.linkText,
      linkUrl: `https://lucid.co${pathTranslations[shortCode]}`,
      storageKey: 'latelyAtLucidBanner',
      content: i18nStrings.content,
      start: dayjs('2024-06-04T00:00:00.000'),
      end: dayjs('2024-06-30T00:00:00.000'),
    },
    // you can set a site specific announcement if you include one
    // this will overwrite the default for that site
    // chart: {
    //   linkText: undefined,
    //   linkUrl: undefined,
    //   storageKey: undefined,
    //   content: undefined,
    //   start: undefined,
    //   end: undefined,
    // },
  };

  const pathname = isBrowser() ? window.location.pathname : '';

  const announcement = announcements[product] || announcements.default || null;
  const now = dayjs();

  // Lots of checks to see if we are good to render the banner or not.
  if (
    // if anything is missing
    !announcement ||
    pathname.includes('/landing') ||
    pathname.includes('/blog') ||
    pathname.includes('/techblog') ||
    !announcement.content ||
    !announcement.linkText ||
    !announcement.linkUrl ||
    !announcement.storageKey ||
    !announcement.start ||
    // if too early or too late
    announcement.start > now ||
    (announcement.end && announcement.end < now)
  ) {
    return null;
  }

  const { linkText, linkUrl, storageKey, content } = announcement;

  const linkProps: LinkProps = {
    iconAnimation: 'navigational',
    underline: 'none',
    label: linkText,
    to: linkUrl,
    variant: 'primaryInverse',
  };
  return (
    <Container
      {...rest}
      css={theme => ({
        padding: 0,
        [mqMin.tablet]: {
          paddingTop: theme.space.s16,
          paddingBottom: theme.space.s8,
          '&:empty': {
            padding: 0,
          },
        },
      })}
      // eslint-disable-next-line react/jsx-sort-props
      className={className}
    >
      <AlertBanner
        willDismissOnLinkClick
        linkProps={linkProps}
        storageKey={storageKey!}
        storageType="local"
        variant="announcement"
      >
        {content}
      </AlertBanner>
    </Container>
  );
};

export { AnnouncementBanner };
export default AnnouncementBanner;
